/* eslint-disable no-underscore-dangle */
import type { html, LitElement } from 'lit';
import { render } from 'lit';
import { TemplateResolver, type TemplateTheme } from '@src/lit/TemplateResolver';

type Constructor<T = object> = new (...args: any[]) => T; // eslint-disable-line @typescript-eslint/no-explicit-any

export type GetTemplateFn = (...params: unknown[]) => ReturnType<typeof html>;
export type DomTargetType = ShadowRoot | HTMLElement;

export declare class TemplatingMixinI {
  getTemplateFn(templateName: string) : GetTemplateFn;
  setTemplatingTheme(templateTheme : TemplateTheme) : void;
  replaceElementsWithTpl(target: DomTargetType, templateName: string, selector: string): void;
  /**
   * @param {boolean} [attributeMode=false] - Call the underlying tooltipTemplate() function with the node or wwith the title and description
   */
  createTooltipInteraction(target: DomTargetType, stylecontext: string, attributeMode?: boolean): void;
}

/**
 * A Mixin that help to work with templates.
 */
export const TemplatingMixin = <T extends Constructor<LitElement>, TemplatesDefinition>(
  superClass: T, theme : TemplateTheme,
  templates: TemplatesDefinition) => {
  class TemplatingMixinClass extends superClass {
    _templateResolver : TemplateResolver<unknown>;

    constructor(...params: any[]) { // eslint-disable-line
      super(params);
      this._templateResolver = TemplateResolver.getInstance(theme, templates);
    }

    // Returns a callable template function where "this" is the actual component instance.
    getTemplateFn<U extends keyof typeof templates[keyof typeof templates]>(templateName: U) {
      const templateFn = this._templateResolver.resolve(templateName);
      return templateFn.bind(this) as GetTemplateFn;
    }

    setTemplatingTheme(templateTheme : TemplateTheme) {
      TemplateResolver.clear();
      this._templateResolver = TemplateResolver.getInstance(templateTheme, templates);
    }

    /**
     * Replaces all elements matching the selector within the target with the rendered template using the current theme (occl/tefOne)
     */
    replaceElementsWithTpl<U extends keyof typeof templates[keyof typeof templates]>(
      target : DomTargetType,
      templateName: U,
      selector: string,
    ) {
      const templateFn = this.getTemplateFn(templateName);
      target.querySelectorAll(selector).forEach((node) => {
        const target$ = new DocumentFragment();
        render(templateFn(node), target$);
        node.replaceWith(target$);
      });
    }

    /**
     * Replaces <abbr> tags with the rendered tooltip template using the current theme (occl/tefOne).
     */
    createTooltipInteraction(target: DomTargetType, stylecontext: string, attributeMode: boolean = false) {
      // @ts-expect-error "This works if a tooltip template exists, which always is the case"
      const tooltipTemplate = this.getTemplateFn('tooltipTemplate');
      const allAbbr: NodeListOf<HTMLElement> = target.querySelectorAll('abbr[title]');
      allAbbr.forEach((node) => {
        const target$ = new DocumentFragment();
        // Either work on a node (tef-coms) or on separated parameters
        // TODO: Unify and make tef.coms4cs work like tef-coms (on node) (FS, 09.11.2025)
        const tplContent = attributeMode ? tooltipTemplate(stylecontext, node.title, node.innerHTML) : tooltipTemplate(stylecontext, node);
        render(tplContent, target$);
        node.replaceWith(target$);
      });
    }
  }
  return TemplatingMixinClass as Constructor<TemplatingMixinI> & T;
};
